import { useMemo, useState } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Info,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { rotuloTipo } from "@/lib/tipoProfissional";
import { trpc } from "@/lib/trpc";
import { formatBRL, formatarData, nomeMes } from "@/lib/utils";
import { Link } from "react-router-dom";

// ============================================================
// F1 — Comissões (somente leitura)
// ============================================================
// Duas metades que hoje vivem separadas, lado a lado pela primeira vez: o que
// a ESCALA prevê pagar de turno e o que a CLÍNICA já apurou de comissão e
// lançou em contas a pagar. Nenhum botão desta tela escreve — fechamento e
// pagamento são a próxima fase.
//
// Regra de leitura que a tela repete de propósito: comissão e conta a pagar
// NÃO se somam. Um fechamento de comissão vira um lançamento em contas a
// pagar; somar os dois contaria o mesmo dinheiro duas vezes.

function reais(centavos: number | null | undefined): string {
  return formatBRL((centavos ?? 0) / 100);
}

export function Comissoes() {
  const hoje = new Date();
  const [ano, setAno] = useState(hoje.getUTCFullYear());
  const [mes, setMes] = useState(hoje.getUTCMonth() + 1);
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);

  const q = trpc.comissoes.resumoPorProfissional.useQuery({ ano, mes, incluirInativos });

  const itens = q.data?.itens ?? [];
  const totais = q.data?.totais;

  const semVinculo = q.data?.avisos.semVinculo ?? [];
  const comErro = q.data?.avisos.comErro ?? [];

  const ordenados = useMemo(() => {
    return [...itens].sort((a, b) => {
      // Quem está sem vínculo sobe: é pendência a resolver, não rodapé.
      if (a.semVinculo !== b.semVinculo) return a.semVinculo ? -1 : 1;
      return a.nome.localeCompare(b.nome, "pt-BR");
    });
  }, [itens]);

  function navegar(delta: number) {
    let novo = mes + delta;
    let novoAno = ano;
    if (novo < 1) {
      novo = 12;
      novoAno--;
    }
    if (novo > 12) {
      novo = 1;
      novoAno++;
    }
    setMes(novo);
    setAno(novoAno);
    setAberto(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Comissões</h1>
          <p className="text-sm text-slate-600">
            Turnos previstos pela escala e comissões apuradas pela clínica, por competência.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={incluirInativos}
              onChange={(e) => setIncluirInativos(e.target.checked)}
            />
            Mostrar inativos
          </label>
          <Button variant="outline" size="sm" onClick={() => q.refetch()} disabled={q.isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${q.isFetching ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button variant="outline" size="icon" onClick={() => navegar(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-44 text-center text-lg font-semibold text-slate-900">
          {nomeMes(mes)} {ano}
        </div>
        <Button variant="outline" size="icon" onClick={() => navegar(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          Esta tela só lê. O valor dos turnos é <strong>previsão</strong>: enquanto o turno não
          tiver marcação de realizado, todo turno agendado do mês entra na conta. Fechar
          competência, pagar e emitir recibo vêm na próxima fase — e continuam sendo feitos pelo
          sistema da clínica, dono do dinheiro. Comissão e conta a pagar aparecem em colunas
          separadas de propósito: somar as duas contaria o mesmo valor duas vezes.
        </span>
      </div>

      {q.data && !q.data.canalClinica && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            O canal com a clínica não está configurado neste ambiente. Os turnos da escala aparecem
            normalmente; comissões e contas a pagar ficam em branco.
          </span>
        </div>
      )}

      {semVinculo.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <strong>{semVinculo.length}</strong>{" "}
            {semVinculo.length === 1 ? "profissional sem vínculo" : "profissionais sem vínculo"} com
            a clínica: {semVinculo.map((s) => s.nome).join(", ")}. Sem o vínculo, nada de comissão
            aparece para essas pessoas — resolva em Vínculos.
          </span>
        </div>
      )}

      {comErro.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            A clínica não respondeu para {comErro.map((c) => c.nome).join(", ")}. A linha fica com
            os turnos, mas sem os números de lá — nada é estimado.
          </span>
        </div>
      )}

      {q.error && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-red-700">Não deu para montar a apuração</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-slate-700">
            <p>{q.error.message}</p>
            <Button variant="outline" onClick={() => q.refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" /> Tentar de novo
            </Button>
          </CardContent>
        </Card>
      )}

      {q.isLoading && <div className="py-10 text-center text-slate-500">Carregando apuração…</div>}

      {!q.isLoading && !q.error && (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label="Turnos no mês" value={String(totais?.turnosQtd ?? 0)} />
            <Stat label="Previsto em turnos" value={reais(totais?.turnosPrevistoCentavos)} />
            <Stat label="Comissões da clínica" value={reais(totais?.comissoesTotalCentavos)} />
            <Stat
              label="Comissões em aberto"
              value={reais(totais?.comissoesEmAbertoCentavos)}
              destaque
            />
          </div>

          {/* Desktop: tabela. Mobile: cards empilhados, mais abaixo. */}
          <Card className="hidden md:block">
            <CardContent className="p-0">
              <div className="relative w-full overflow-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50">
                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-4 py-3">Profissional</th>
                      <th className="px-4 py-3 text-right">Turnos</th>
                      <th className="px-4 py-3 text-right">Previsto</th>
                      <th className="px-4 py-3 text-right">Comissões</th>
                      <th className="px-4 py-3 text-right">Já pago</th>
                      <th className="px-4 py-3 text-right">Em aberto</th>
                      <th className="px-4 py-3 text-right">Contas a pagar</th>
                      <th className="w-10 px-2 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordenados.map((i) => (
                      <LinhaProfissional
                        key={i.profissionalId}
                        item={i}
                        ano={ano}
                        mes={mes}
                        aberto={aberto === i.profissionalId}
                        onToggle={() =>
                          setAberto(aberto === i.profissionalId ? null : i.profissionalId)
                        }
                      />
                    ))}
                    {!ordenados.length && (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                          Nenhum profissional nesta competência.
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {ordenados.length > 0 && (
                    <tfoot className="border-t-2 border-slate-200 bg-slate-50">
                      <tr className="font-semibold text-slate-800">
                        <td className="px-4 py-3">Totais</td>
                        <td className="px-4 py-3 text-right">{totais?.turnosQtd ?? 0}</td>
                        <td className="px-4 py-3 text-right">{reais(totais?.turnosPrevistoCentavos)}</td>
                        <td className="px-4 py-3 text-right">{reais(totais?.comissoesTotalCentavos)}</td>
                        <td className="px-4 py-3 text-right">{reais(totais?.comissoesPagoCentavos)}</td>
                        <td className="px-4 py-3 text-right">{reais(totais?.comissoesEmAbertoCentavos)}</td>
                        <td className="px-4 py-3 text-right">{reais(totais?.contasEmAbertoCentavos)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3 md:hidden">
            {ordenados.map((i) => (
              <CardProfissional
                key={i.profissionalId}
                item={i}
                ano={ano}
                mes={mes}
                aberto={aberto === i.profissionalId}
                onToggle={() => setAberto(aberto === i.profissionalId ? null : i.profissionalId)}
              />
            ))}
            {ordenados.length > 0 && (
              <Card>
                <CardContent className="space-y-1 p-4 text-sm">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Totais do mês
                  </div>
                  <Linha rotulo="Turnos" valor={String(totais?.turnosQtd ?? 0)} />
                  <Linha rotulo="Previsto em turnos" valor={reais(totais?.turnosPrevistoCentavos)} />
                  <Linha rotulo="Comissões" valor={reais(totais?.comissoesTotalCentavos)} />
                  <Linha rotulo="Já pago" valor={reais(totais?.comissoesPagoCentavos)} />
                  <Linha rotulo="Em aberto" valor={reais(totais?.comissoesEmAbertoCentavos)} />
                  <Linha rotulo="Contas a pagar em aberto" valor={reais(totais?.contasEmAbertoCentavos)} />
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}

type Item = {
  profissionalId: number;
  nome: string;
  tipo: string;
  ativo: boolean;
  semVinculo: boolean;
  erroClinica: string | null;
  turnos: { qtd: number; valorPrevistoCentavos: number; porStatus: Record<string, { qtd: number; valorCentavos: number }> };
  // F2/F3: a conferencia do mes decide se da para fechar competencia.
  conferencia: { status: string; totalApuradoCentavos: number } | null;
  comissoes: { totalCentavos: number; pagoCentavos: number; emAbertoCentavos: number; qtd: number };
  contas: { pagoCentavos: number; emAbertoCentavos: number; qtd: number };
  fechamentos: { qtd: number; valorTotalCentavos: number };
};

function LinhaProfissional({
  item,
  ano,
  mes,
  aberto,
  onToggle,
}: {
  item: Item;
  ano: number;
  mes: number;
  aberto: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={`cursor-pointer border-b border-slate-100 hover:bg-slate-50 ${
          item.semVinculo ? "bg-red-50/60" : ""
        }`}
        onClick={onToggle}
      >
        <td className="px-4 py-3 align-top">
          <div className={item.semVinculo ? "font-medium text-red-700" : "font-medium text-slate-900"}>
            {item.nome}
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            <Badge variant="outline">{rotuloTipo(item.tipo)}</Badge>
            {!item.ativo && <Badge variant="inativo">inativo</Badge>}
            {item.semVinculo && (
              <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                sem vínculo com a clínica
              </span>
            )}
            {item.erroClinica && (
              <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                clínica não respondeu
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3 text-right align-top text-slate-700">{item.turnos.qtd}</td>
        <td className="px-4 py-3 text-right align-top text-slate-700">
          {reais(item.turnos.valorPrevistoCentavos)}
        </td>
        <td className="px-4 py-3 text-right align-top font-medium text-slate-900">
          {item.semVinculo ? "—" : reais(item.comissoes.totalCentavos)}
        </td>
        <td className="px-4 py-3 text-right align-top text-emerald-700">
          {item.semVinculo ? "—" : reais(item.comissoes.pagoCentavos)}
        </td>
        <td className="px-4 py-3 text-right align-top text-slate-900">
          {item.semVinculo ? "—" : reais(item.comissoes.emAbertoCentavos)}
        </td>
        <td className="px-4 py-3 text-right align-top text-slate-600">
          {item.semVinculo ? "—" : reais(item.contas.emAbertoCentavos)}
        </td>
        <td className="px-2 py-3 text-right align-top text-slate-400">
          {aberto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </td>
      </tr>
      {aberto && (
        <tr className="border-b border-slate-100 bg-slate-50/60">
          <td colSpan={8} className="px-4 py-4">
            <Detalhe
              profissionalId={item.profissionalId}
              ano={ano}
              mes={mes}
              nome={item.nome}
              conferencia={item.conferencia}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function CardProfissional({
  item,
  ano,
  mes,
  aberto,
  onToggle,
}: {
  item: Item;
  ano: number;
  mes: number;
  aberto: boolean;
  onToggle: () => void;
}) {
  return (
    <Card className={item.semVinculo ? "border-red-200" : ""}>
      <CardContent className="p-4">
        <button className="flex w-full items-start justify-between gap-2 text-left" onClick={onToggle}>
          <div>
            <div className={item.semVinculo ? "font-medium text-red-700" : "font-medium text-slate-900"}>
              {item.nome}
            </div>
            <div className="mt-1 flex flex-wrap gap-1">
              <Badge variant="outline">{rotuloTipo(item.tipo)}</Badge>
              {!item.ativo && <Badge variant="inativo">inativo</Badge>}
              {item.semVinculo && (
                <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                  sem vínculo
                </span>
              )}
            </div>
          </div>
          <span className="mt-1 text-slate-400">
            {aberto ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </span>
        </button>

        <div className="mt-3 space-y-1 text-sm">
          <Linha rotulo="Turnos" valor={String(item.turnos.qtd)} />
          <Linha rotulo="Previsto" valor={reais(item.turnos.valorPrevistoCentavos)} />
          <Linha
            rotulo="Comissões"
            valor={item.semVinculo ? "—" : reais(item.comissoes.totalCentavos)}
          />
          <Linha rotulo="Já pago" valor={item.semVinculo ? "—" : reais(item.comissoes.pagoCentavos)} />
          <Linha
            rotulo="Em aberto"
            valor={item.semVinculo ? "—" : reais(item.comissoes.emAbertoCentavos)}
          />
          <Linha
            rotulo="Contas a pagar"
            valor={item.semVinculo ? "—" : reais(item.contas.emAbertoCentavos)}
          />
        </div>

        {aberto && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <Detalhe
              profissionalId={item.profissionalId}
              ano={ano}
              mes={mes}
              nome={item.nome}
              conferencia={item.conferencia}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Detalhe({
  profissionalId,
  ano,
  mes,
  nome,
  conferencia,
}: {
  profissionalId: number;
  ano: number;
  mes: number;
  nome: string;
  conferencia: { status: string; totalApuradoCentavos: number } | null;
}) {
  const q = trpc.comissoes.detalhe.useQuery({ profissionalId, ano, mes });

  if (q.isLoading) return <div className="py-4 text-sm text-slate-500">Carregando detalhe…</div>;
  if (q.error) return <div className="py-4 text-sm text-red-700">{q.error.message}</div>;

  const d = q.data;
  if (!d) return null;

  return (
    <div className="space-y-5">
      {d.erroClinica && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {d.erroClinica}
        </div>
      )}
      {d.semVinculo && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          Este profissional não está vinculado a nenhuma ficha da clínica. Só os turnos da escala
          aparecem aqui.
        </div>
      )}

      <Bloco titulo={`Turnos da escala (${d.turnos.length})`} vazio="Nenhum turno no mês.">
        {d.turnos.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {d.turnos.map((t) => (
              <li key={t.turnoId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="flex items-center gap-2 text-slate-700">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.tipoTurnoCor }} />
                  {formatarData(t.data)} · {t.tipoTurnoNome} · {t.horaInicio}–{t.horaFim}
                  <Badge variant="outline">{t.status}</Badge>
                </span>
                <span className="font-medium text-slate-900">{reais(t.valorCentavos)}</span>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco
        titulo={`Comissões da clínica (${d.comissoes.length})`}
        vazio="Nenhuma comissão apurada neste mês."
      >
        {d.comissoes.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {d.comissoes.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="text-slate-700">
                  {c.dataVenda ? formatarData(c.dataVenda.slice(0, 10)) : "sem data"} ·{" "}
                  {c.produtoNome ?? "item sem nome"}
                  {c.papel ? ` · ${c.papel}` : ""}
                  {c.percentual != null ? ` · ${c.percentual}%` : ""}{" "}
                  <Badge variant="outline">{c.status}</Badge>
                </span>
                <span className="font-medium text-slate-900">{reais(c.valorComissaoCentavos)}</span>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco
        titulo={`Fechamentos de comissão (${d.fechamentos.length})`}
        vazio="Nenhum extrato fechado."
      >
        {d.fechamentos.length > 0 && (
          <ul className="divide-y divide-slate-100">
            {d.fechamentos.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="text-slate-700">
                  #{f.id} · {f.dataInicio ? formatarData(f.dataInicio.slice(0, 10)) : "—"} a{" "}
                  {f.dataCorte ? formatarData(f.dataCorte.slice(0, 10)) : "—"} · {f.qtdItens} itens{" "}
                  <Badge variant="outline">{f.status}</Badge>
                </span>
                <span className="font-medium text-slate-900">{reais(f.valorTotalCentavos)}</span>
              </li>
            ))}
          </ul>
        )}
      </Bloco>

      <Bloco
        titulo={`Contas a pagar na clínica (${d.contas.length})`}
        vazio="Nenhuma conta a pagar em nome deste prestador."
      >
        {d.contas.length > 0 && (
          <>
            <ul className="divide-y divide-slate-100">
              {d.contas.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-slate-700">
                    {c.dataVencimento ? formatarData(c.dataVencimento.slice(0, 10)) : "sem vencimento"} ·{" "}
                    {c.descricao} <Badge variant="outline">{c.status}</Badge>
                    {c.origem === "nome_fornecedor" && (
                      <span className="ml-1 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
                        casada por nome
                      </span>
                    )}
                  </span>
                  <span className="font-medium text-slate-900">{reais(c.valorCentavos)}</span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-slate-500">
              As linhas marcadas como "casada por nome" não têm vínculo forte com o prestador: o
              lançamento da clínica não guarda o id da pessoa, só o nome do fornecedor. Confira
              antes de tratar como dívida com ele.
            </p>
          </>
        )}
      </Bloco>

      {/* R4 (08/09) — fechar competencia, pagar, vincular conta lancada,
          emitir recibo e estornar MUDARAM DE ENDERECO. Nao foram duplicados:
          dinheiro de turno agora se mexe num lugar so, /pagamentos, que tem
          filtro por setor e totais no rodape. Esta tela voltou a ser o que o
          nome diz — o que a clinica apurou de comissao. */}
      <Bloco titulo="Pagamento dos turnos" vazio="">
        <p className="text-slate-600">
          {conferencia?.status === "conferido"
            ? `Conferência de ${nome} aceita: ${reais(conferencia.totalApuradoCentavos)}.`
            : `A competência de ${nome} ainda não foi conferida.`}{" "}
          Fechar competência, registrar pagamento, vincular conta lançada, emitir recibo e
          estornar ficam em{" "}
          <Link to="/pagamentos" className="font-medium text-brand-700 underline">
            Pagamentos
          </Link>
          .
        </p>
      </Bloco>
    </div>
  );
}

function Bloco({
  titulo,
  vazio,
  children,
}: {
  titulo: string;
  vazio: string;
  children?: React.ReactNode;
}) {
  const temConteudo = Boolean(children);
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{titulo}</div>
      {temConteudo ? (
        <div className="text-sm">{children}</div>
      ) : (
        <div className="py-2 text-sm text-slate-400">{vazio}</div>
      )}
    </div>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{rotulo}</span>
      <span className="font-medium text-slate-900">{valor}</span>
    </div>
  );
}

function Stat({ label, value, destaque }: { label: string; value: string; destaque?: boolean }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
        <div className={`mt-1 text-2xl font-bold ${destaque ? "text-brand-600" : "text-slate-900"}`}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
